import React, { useState } from "react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { z as zod } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Field, Form } from "@onair/minimal-ui/hook-form";
import { Box } from "@mui/material";
import { toast } from "@onair/minimal-ui/snackbar";
// import { useUpdateResourceMutation } from "#src/operations";
import { useUpdateResourceMutation } from "@onair/operations";
import { normalizeExt } from "@onair/utils/upload-to-s3";
export type ResourceUpdateSchemaType = zod.infer<typeof ResourceUpdateSchema>;

export const ACCEPTED_EXTENSIONS = [
	"mov",
	"mp4",
	"mpg",
	"flv",
	"f4v",
	"m4v",
	"asf",
	"wmv",
	"vob",
	"3gp",
	"mkv",
	"divx",
	"xvid",
	"png",
	"jpg",
	"jpeg",
	"pdf",
	"xls",
	"xlsx",
	"docx",
];

export const ResourceUpdateSchema = zod.object({
	fileName: zod
		.string()
		.trim()
		.min(1, { message: "Vui lòng nhập tên tệp!" })
		.max(255, { message: "Vui lòng nhập tối đa 255 ký tự" })
		.refine((val) => !/[\n\t]/.test(val), {
			message: "Tên tệp không được chứa ký tự xuống dòng hoặc tab",
		})
		.refine(
			(val) => {
				const parts = val.split(".");
				if (parts.length < 2) return false;

				const ext = parts.pop()?.toLowerCase();
				return ACCEPTED_EXTENSIONS.includes(ext!);
			},
			{
				message: "Định dạng tệp không hợp lệ",
			},
		),
});

interface IUpdateResourceModalProps {
	id: string;
	fileName: string;
	fileType: string;
	children: React.ReactNode;
	refetch: () => void;
}

const UpsetModalResource = ({
	children,
	id,
	fileName,
	fileType,
	refetch,
}: IUpdateResourceModalProps) => {
	const [openDialog, setOpenDialog] = useState(false);

	const methods = useForm({
		mode: "all",
		resolver: zodResolver(ResourceUpdateSchema),
		defaultValues: {
			fileName: fileName,
		},
	});

	const {
		watch,
		reset,
		control,
		setValue,
		resetField,
		handleSubmit,
		formState: { isSubmitting, isValid },
	} = methods;

	const handleCloseDialog = () => {
		setOpenDialog(false);
		resetField("fileName");
	};

	const { mutateAsync: updateResourceMutationAsync } =
		useUpdateResourceMutation({
			onError: () => toast.error("Có lỗi xảy ra. Vui lòng thử lại sau"),
			onSuccess: () => {
				refetch();
				setOpenDialog(false);
				resetField("fileName");
				toast.success("Sửa tệp thành công");
			},
		});

	const onSubmit = handleSubmit(async (data) => {
		try {
			const originalExt = normalizeExt(fileType.split("/").pop());
			const newExt = normalizeExt(data.fileName.split(".").pop());

			if (originalExt !== newExt) {
				toast.error("Không được phép đổi phần mở rộng của tệp!");
				return;
			}

			await updateResourceMutationAsync({
				id,
				data: {
					fileName: data.fileName,
				},
			});
		} catch (error) {
			console.log(error);
		}
	});

	return (
		<>
			{children ? (
				<Box onClick={() => setOpenDialog(true)}>{children}</Box>
			) : null}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
				aria-labelledby="resource-dialog-title"
				aria-describedby="resource-dialog-description"
			>
				<DialogTitle id="alert-dialog-title">Chỉnh sửa tệp</DialogTitle>

				<DialogContent>
					<Form methods={methods} onSubmit={onSubmit}>
						<Field.Text name="fileName" placeholder="Tên Tệp" />
					</Form>
				</DialogContent>

				<DialogActions>
					<Stack direction="row" spacing={1}>
						<Button onClick={handleCloseDialog} variant="outlined">
							Đóng
						</Button>
						<Button
							color="primary"
							variant="contained"
							type="submit"
							onClick={onSubmit}
							disabled={!isValid || isSubmitting}
						>
							Chỉnh sửa
						</Button>
					</Stack>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default UpsetModalResource;
